<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WahaCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WahaCampaign/WahaCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WahaCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WahaCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showWahaCampaignDialog, toggleWahaCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WahaCampaigns = computed(
  () => getters['campaigns/getWahaCampaigns'].value
);

const hasNoWahaCampaigns = computed(
  () => WahaCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handlePause = async campaign => {
  console.log('handlePause called with campaign:', campaign);
  try {
    await store.dispatch('campaigns/pause', campaign.id);
    console.log('Pause successful');
  } catch (error) {
    console.error('Failed to pause campaign:', error);
  }
};

const handleResume = async campaign => {
  console.log('handleResume called with campaign:', campaign);
  try {
    await store.dispatch('campaigns/resume', campaign.id);
    console.log('Resume successful');
  } catch (error) {
    console.error('Failed to resume campaign:', error);
  }
};

const handleStop = async campaign => {
  console.log('handleStop called with campaign:', campaign);
  try {
    await store.dispatch('campaigns/stop', campaign.id);
    console.log('Stop successful');
  } catch (error) {
    console.error('Failed to stop campaign:', error);
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WAHA.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WAHA.NEW_CAMPAIGN')"
    @click="toggleWahaCampaignDialog()"
    @close="toggleWahaCampaignDialog(false)"
  >
    <template #action>
      <WahaCampaignDialog
        v-if="showWahaCampaignDialog"
        @close="toggleWahaCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWahaCampaigns"
      :campaigns="WahaCampaigns"
      @delete="handleDelete"
      @pause="handlePause"
      @resume="handleResume"
      @stop="handleStop"
    />
    <WahaCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WAHA.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WAHA.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
